import {Component, EventEmitter, Input, Output, ViewChild, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import Fund from "../../../../interfaces/fund";
import {MatDialog} from "@angular/material/dialog";
import {AssignmentsModalComponent} from "../../../../components/assignments-modal/assignments-modal.component";
import {AddProjectCreditComponent} from "../../../add-project-credit/add-project-credit.component";
import {DatePipe} from "@angular/common";
import {animate, state, style, transition, trigger} from '@angular/animations';
import {Apollo} from "apollo-angular";
import {get_outlet_project_attribute} from "../../../graphql/getFunds";

export interface FundCreditTable {
  project: string,
  credit_type: string,
  admitted_date: string,
  exit_date: string,
  location: string,
  msa: string,
  sponsor: string,
  gross_equity: number,
  revenue_year: number,
  status: string,
  ppc: number,
  yield_percent: number,
  hard_debt: number,
  owned: number
  load: number,
  investor_approval_date: string,
  ut_irr: number,
  closing_date: string
}

@Component({
    selector: 'fund-credits-table',
    templateUrl: './fund-credits-table.component.html',
    styleUrls: ['./fund-credits-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FundCreditsTableComponent implements OnInit{
  public creditColumns:string[] = ['actions', 'project', 'credit_type', 'gross_equity', 'owned', 'status', 'closing_date', 'admitted_date', 'revenue_year', 'exit_date', 'sponsor', 'msa', 'location', 'ppc', 'yield_percent',
  'hard_debt', 'investor_approval_date']
  @Input() outlets:{[key:string]: any}
  @Input() fundProjectStates: any[] = []
  @Input() permissions:any;
  @Input() statusSequences:any[] = [];
  @ViewChild(MatSort) sort: MatSort
  public isExpanded:boolean = true;
  public detailsLoaded:boolean = false;
  public totals:any;
  expandedElement: any | null = null;
  @Output('save') saveForm: EventEmitter<any> = new EventEmitter<any>()
  @Output('update') updateForm: EventEmitter<any> = new EventEmitter<any>()
  @Output('remove') removeForm: EventEmitter<any> = new EventEmitter<any>()
  @Output('subscribe') subscription: EventEmitter<any> = new EventEmitter<any>()
  constructor(private dialog: MatDialog, private datePipe: DatePipe, private apollo:Apollo) {
  }

  ngOnInit() {
   this.totals = this.getCreditTotals()
  }

  public getDataSource(credits:any){
    let source = new MatTableDataSource<FundCreditTable>(credits)
    source.sort = this.sort;
    return source
  }

  public getCreditTotals(){
    let proj = 0;
    let avgDebt = 0;
    let avgPpc = 0;
    let totalPpc = 0;
    let ppcDivisible = 0;
    let totalLihtcUnits = 0
    let totalUnits = 0;
    let totalDebt = 0;
    let netEquity = 0;
    let totalEquity = 0;
    let debtDivisible = 0;
    let keys = Object.keys(this.outlets);
    let creditsArr:any = []
    let testNum = 0;
    for(let k = 0; k < keys.length; k++){
      let status = this.outlets[keys[k]]
      let types = Object.keys(status);
      for(let i = 0; i < types.length; i++){
        let arr = this.outlets[keys[k]][types[i]];
        creditsArr.push(...arr)
      }
    }
    creditsArr.forEach((cr:any) => {
      totalEquity += cr.estimated_gross_equity ?? cr.gross_equity

      if(cr.ppc!==null){
        ppcDivisible += cr.gross_equity
        totalPpc += (cr.ppc * cr.gross_equity)
      }
      if(cr.hard_debt!==null){
        totalDebt += (cr.hard_debt * cr.gross_equity)
        debtDivisible += cr.gross_equity
      }

      netEquity += cr.equity

    })
    let condensedArr = creditsArr.filter((item:any, index:number, self:any) => {
      return self.findIndex((i:any) => i.project === item.project) === index;
    })
    condensedArr.forEach((cr:any) => {
      proj += 1;
      totalLihtcUnits += cr.total_lihtc_units
      totalUnits += cr.total_units

    })
    avgPpc = totalPpc / ppcDivisible
    avgDebt = totalDebt / debtDivisible;
    return {proj: proj, net_equity: netEquity, gross_equity: totalEquity, avg_ppc: avgPpc, avg_debt: avgDebt, total_units: totalUnits, lihtc_units: totalLihtcUnits}
  }

  public openAssignmentsModal(row:any){
    let dialogRef = this.dialog.open(AssignmentsModalComponent, {
      width: "1200px",
      data: {
        projectCreditId: row.credit_id,
        projectId: row.projectid,
        project: row.project,
        status: row.syndicationsStatus,
        projecturl: row.projecturl,
        sponsor: row.sponsor,
        closing_date: row.closing_date,
        sponsorurl: row.sponsorurl,
        fundProjectStates: this.fundProjectStates,
        sequenceStatuses: this.statusSequences,
        type: 'Fund'
      }
    })
    let componentInstance = dialogRef.componentInstance.onSave.subscribe((data:any) => {
      let keys = Object.keys(data);
      let outlets:any[] = []
      for(let i = 0; i < keys.length; i++){
        outlets.push(...data[keys[i]]);
      }
      let outletData:any[] = []
      for(let i = 0; i < outlets.length; i++){
        if(outlets[i]){
          let outletObj = {
            ut_irr: outlets[i].value.ut_irr,
            revenue_year: outlets[i].value.revenue_year,
            ownership: outlets[i].value.ownership,
            credit_id: outlets[i].value.credit_id,
            outlet_id: outlets[i].value.outlet_id,
            equity_amount: outlets[i].value.gross_equity,
            estimated_gross_equity: outlets[i].value.estimated_gross_equity,
            load_percentage: outlets[i].value.load,
            admitted_date: this.datePipe.transform(outlets[i].value.admitted_date, 'yyyy-MM-dd'),
            is_target_exit: outlets[i].value.is_target_exit,
            is_target: outlets[i].value.is_target,
            exit_date: this.datePipe.transform(outlets[i].value.exit_date, 'yyyy-MM-dd'),
            fund_project_state_id: outlets[i].value.placement_status,
            fund_id: outlets[i].value.fund_id,
            certificated_outlet_id: outlets[i].value.certificated_outlet_id,
            warehoused: outlets[i].value.warehoused,
            investor_approval_date: this.datePipe.transform(outlets[i].value.investor_approval_date,'yyyy-MM-dd'),
            projected_warehouse_days: outlets[i].value.projected_warehouse_days,
            is_target_investor_approval: outlets[i].value.is_target_investor_approval
          }
          outletData.push(outletObj)
        }
      }

     this.updateForm.emit(outletData)
      dialogRef.close()
    })

    let removeInstance = dialogRef.componentInstance.onRemove.subscribe((data:string)=>{
      this.removeForm.emit(data)
    })

    let subscribeInstance = dialogRef.componentInstance.onSubscribe.subscribe((data:any) => {
      this.subscription.emit()
    })

  }

  public openAddCreditModal(){
    let dialogRef = this.dialog.open(AddProjectCreditComponent, {
      width: '900px',
      data: {
        fundProjectStates: this.fundProjectStates,

      }
    })
    let componentInstance = dialogRef.componentInstance.saveCredits.subscribe((credits:any) => {
      this.saveForm.emit(credits)
      dialogRef.close()
    })
  }

  public toggleExpandedRow(row:any, odd:any){
    row.isExpanded = row.isExpanded ? false : true;
    if(row.isExpanded){
      // if(this.expandedElement!==null){
      //   this.expandedElement.isExpanded = false
      // }
      // if(row.outlet_id !== this.expandedElement?.outlet_id){
      //   this.expandedElement = row;
      // }
      this.detailsLoaded = false;
      this.apollo.watchQuery({
        query: get_outlet_project_attribute,
        variables: {
          id: row.projectid
        }
      }).valueChanges.subscribe((data:any) => {
        let attributes = data.data.project.projectAttributes
        row.resident = attributes.filter((attr:any) => attr.attribute.active==true && attr.attribute.attributeType.name=='Resident')
        row.construction = attributes.filter((attr:any) => attr.attribute.active==true && attr.attribute.attributeType.name=='Construction')
        this.detailsLoaded = true;
      })

    }
  }

  public getSectionHeader(key:any){
    let firstSplit = key.split(" ")[0]
    if(firstSplit.toLowerCase() === 'federal'){
      return "section-header-federal"
    }
    if(firstSplit.toLowerCase() === 'state'){
      return "section-header-state"
    }
    return "section-header-federal"
  }

  public defaultOrder(a:any, b:any){
    return 0;
  }

  public sortData(key:any, stateKey:any, event:any){
    let field = event.active
    let direction = event.direction
    this.outlets[stateKey][key] = this.outlets[stateKey][key].sort((a:any, b:any) => {
      let aVal = a[field] ?? ""
      let bVal = b[field] ?? ""
      if(field==='credit_type'){
        aVal = a.credit_type + (a.credit_designation ? " " + a.credit_designation : "")
        bVal = b.credit_type + (b.credit_designation ? " " + b.credit_designation : "")
      }
      if(direction == 'asc'){
        return aVal.toString().localeCompare(bVal.toString())
      } else if(direction == 'desc'){
        return bVal.toString().localeCompare(aVal.toString())
      } else {
        return 0
      }
    })

  }

  protected readonly Object = Object;
}
